import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Loader2, Check, User, Lock, Mail } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivatePatientPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doctor/activate/${token}`);
        setData(data);
        if (data.email) setEmail(data.email);
      } catch (e) {
        setError(e.response?.data?.detail || (bg ? "Невалиден или изтекъл линк" : "Invalid or expired link"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(bg ? "Паролата трябва да е поне 8 знака" : "Password must be at least 8 chars");
      return;
    }
    if (data.requires_email && !email) {
      toast.error(bg ? "Въведете имейл" : "Enter an email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/doctor/activate/${token}/complete`, {
        password,
        email: data.requires_email ? email : undefined,
      });
      setDone(true);
      toast.success(bg ? "Акаунтът е активиран!" : "Account activated!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при активация" : "Activation error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4
      ${isDark ? "bg-[hsl(222,47%,6%)]" : "bg-gradient-to-br from-violet-50 via-fuchsia-50 to-sky-50"}`}
      data-testid="activate-patient-page">
      <SEO
        title={bg ? "Активиране на акаунт — Nutramilo" : "Activate Account — Nutramilo"}
        description={bg ? "Активирайте акаунта си, създаден от вашия лекар." : "Activate the account created by your doctor."}
        path={`/activate/${token}`}
      />

      <div className={`w-full max-w-md rounded-2xl shadow-xl p-6 sm:p-8
        ${isDark ? "bg-[hsl(222,47%,10%)] text-white" : "bg-white text-gray-900"}`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-3">
              ✕
            </div>
            <h2 className="text-lg font-bold mb-2">{bg ? "Грешка" : "Error"}</h2>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{error}</p>
          </div>
        ) : done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-lg font-bold mb-2">{bg ? "Успешно активиране" : "Activation successful"}</h2>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {bg ? "Пренасочваме ви към вход..." : "Redirecting to login..."}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-5">
              <img src="/nutramilo-logo.png" alt="Nutramilo" className="mx-auto mb-3 h-12 w-auto object-contain" />
              <h2 className="text-xl font-bold mb-1">
                {bg ? "Добре дошли" : "Welcome"}{data?.name ? `, ${data.name}` : ""}!
              </h2>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "Вашият лекар създаде акаунт за вас. Задайте парола, за да влезете."
                    : "Your doctor created an account for you. Set a password to log in."}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {data?.requires_email ? (
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {bg ? "Имейл" : "Email"}
                  </label>
                  <div className="relative">
                    <Mail className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border outline-none
                        ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white" : "bg-gray-50 border-gray-200"}`}
                      data-testid="activate-email-input"
                    />
                  </div>
                </div>
              ) : data?.email && (
                <div className={`rounded-lg p-2.5 text-xs flex items-center gap-2
                  ${isDark ? "bg-[hsl(222,47%,8%)] text-gray-400" : "bg-gray-50 text-gray-600"}`}>
                  <User className="w-3.5 h-3.5" />
                  {data.email}
                </div>
              )}

              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {bg ? "Парола (поне 8 знака)" : "Password (min 8 chars)"}
                </label>
                <div className="relative">
                  <Lock className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border outline-none
                      ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white" : "bg-gray-50 border-gray-200"}`}
                    data-testid="activate-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-xl
                  bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold
                  hover:opacity-95 disabled:opacity-60"
                data-testid="activate-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {bg ? "Активирай акаунт" : "Activate account"}
              </button>
            </form>

            <p className={`text-[10px] text-center mt-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "Получавате 30 дни пълен Pro достъп, предоставен от вашия лекар."
                  : "You receive 30 days of full Pro access provided by your doctor."}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
